import {
	type ElasticsearchQuery,
	type ElasticsearchResponseBody,
	type SearchRequest,
} from '@searchkit/api';

import { type RequestContext } from '~/types/RequestContext';

import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import { type BettingInsightsResultAttribute, type EsIndex } from '../types';

export interface BettingInsightsElasticSearchConfig<TIndex extends EsIndex> {
	afterSearch?: (
		this: BettingInsightsElasticSearch<TIndex>,
		requests: SearchRequest[],
		responses: ElasticsearchResponseBody[],
	) => Promise<ElasticsearchResponseBody[]>;
	beforeSearch?: (
		this: BettingInsightsElasticSearch<TIndex>,
		searchRequests: SearchRequest[],
	) => Promise<SearchRequest[]>;
	facetAttributes: BettingInsightsFacetAttribute<TIndex>[];
	getBaseFilters?: (
		this: BettingInsightsElasticSearch<TIndex>,
	) => ElasticsearchQuery[];
	results: {
		attributes: BettingInsightsResultAttribute<TIndex>[];
	};
	sorting?: (
		this: BettingInsightsElasticSearch<TIndex>,
	) => BettingInsightsSortConfig<TIndex>[];
}

export class BettingInsightsElasticSearch<
	TIndex extends EsIndex,
> extends BettingInsightsComponent {
	afterSearch?: (
		requests: SearchRequest[],
		responses: ElasticsearchResponseBody[],
	) => Promise<ElasticsearchResponseBody[]>;
	beforeSearch?: (searchRequests: SearchRequest[]) => Promise<SearchRequest[]>;
	facetAttributes: BettingInsightsFacetAttribute<TIndex>[];
	getBaseFilters?: () => ElasticsearchQuery[];
	results: {
		attributes: BettingInsightsResultAttribute<TIndex>[];
	};
	sorting?: () => BettingInsightsSortConfig<TIndex>[];

	constructor({
		afterSearch,
		beforeSearch,
		facetAttributes,
		getBaseFilters,
		results,
		sorting,
		...requestContext
	}: BettingInsightsElasticSearchConfig<TIndex> & RequestContext) {
		super(requestContext);
		this.afterSearch = afterSearch?.bind(this);
		this.beforeSearch = beforeSearch?.bind(this);
		this.facetAttributes = facetAttributes;
		this.getBaseFilters = getBaseFilters?.bind(this);
		this.results = results;
		this.sorting = sorting?.bind(this);
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsElasticSearchConfig<TIndex>,
	) {
		return new this<TIndex>({
			...requestContext,
			...config,
		});
	}
}

export type BettingInsightsSortConfig<TIndex extends EsIndex> = {
	field: BettingInsightsResultAttribute<TIndex>;
	label: string;
	order: 'asc' | 'desc';
};

export type BettingInsightsFacetAttribute<
	TIndex extends EsIndex,
	TAttribute = BettingInsightsResultAttribute<TIndex>,
> = {
	attribute: TAttribute;
	field: TAttribute;
	type?: 'numeric' | 'string' | 'date';
};
